import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { css, StyleSheet } from 'aphrodite';
import cn from 'classnames';
import Input from '@components/atoms/ui-kit/input.tsx';
import { PATHS } from '../router/paths.ts';
import { useResetPassword } from '@utils/api/users/use-reset-password.tsx';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import PublicLogo from '@components/shared/public-logo.tsx';
import { useMemo } from 'react';

interface ResetPasswordFormValues {
  email: string;
  reset_key: string;
  password: string;
  password_confirmation: string;
}

const PasswordCriteria = ({ password }: { password: string }) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const criteria = [
    {
      label: t('password-must-be-at-least-12-characters-long'),
      valid: password.length >= 12
    },
    {
      label: t('password-must-have-at-least-one-uppercase-letter'),
      valid: /[A-Z]/.test(password)
    },
    {
      label: t('password-must-have-at-least-one-lowercase-letter'),
      valid: /[a-z]/.test(password)
    },
    {
      label: t('password-must-have-at-least-one-number'),
      valid: /\d/.test(password)
    }
  ];

  /* eslint-disable no-restricted-syntax -- Semantic validation success color */
  return (
    <ul className="text-sm mt-2 space-y-1.5">
      {criteria.map((item, idx) => (
        <li
          key={idx}
          style={{
            color: item.valid ? '#22c55e' : getColour('inactiveText')
          }}
        >
          {item.valid ? '✓' : '○'} {item.label}
        </li>
      ))}
    </ul>
  );
  /* eslint-enable no-restricted-syntax */
};

const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const navigate = useNavigate();
  const { mutate, isLoading } = useResetPassword();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const activationKey = searchParams.get('key');

  const styles = useMemo(
    () =>
      StyleSheet.create({
        registerContainer: {
          backgroundColor: getColour('secondLayer')
        },
        registerText: {
          color: getColour('normalText')
        },
        iconsColor: {
          color: getColour('icon')
        }
      }),
    [getColour]
  );

  const validationSchema = Yup.object({
    email: Yup.string().email(t('invalid-email')).required(t('email-required')),
    reset_key: Yup.string().required(t('activation-key-required')),
    password: Yup.string()
      .required(t('new-password-is-required'))
      .min(12, t('password-must-be-at-least-12-characters-long'))
      .matches(/[A-Z]/, t('password-must-have-at-least-one-uppercase-letter'))
      .matches(/[a-z]/, t('password-must-have-at-least-one-lowercase-letter'))
      .matches(/\d/, t('password-must-have-at-least-one-number')),
    password_confirmation: Yup.string()
      .required(t('confirm-password-is-required'))
      .oneOf([Yup.ref('password')], t('passwords-must-match'))
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid }
  } = useForm<ResetPasswordFormValues>({
    defaultValues: {
      email: email || '',
      reset_key: activationKey || '',
      password: '',
      password_confirmation: ''
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange' // enable real-time validation
  });

  const passwordValue = watch('password');

  const onSubmit = (values: ResetPasswordFormValues) => {
    mutate(
      {
        email: values.email,
        reset_key: values.reset_key,
        password: values.password
      },
      {
        onSuccess: () => {
          toast.success(t('activation-successful'));
          setTimeout(() => {
            navigate(PATHS().LOGIN);
          }, 500);
        },
        onError: (error: AxiosError<BaseError>) => {
          const errorMessage =
            error.response?.data?.error_description ||
            error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            t('activation-failed');

          toast.error(errorMessage);
        }
      }
    );
  };

  return (
    <div
      className={cn(
        'py-24 px-2 lg:px-8 h-full',
        css(styles.registerContainer, styles.registerText)
      )}
    >
      <div className="flex flex-col gap-y-5 justify-center items-center mb-4">
        <PublicLogo className="h-auto w-[11rem]" />
        <h1 className="text-2xl font-bold leading-9 tracking-tight">
          {t('password-reset')}
        </h1>
        <div>
          <p className="text-md py-2 items-center text-center">
            {t('already-have-account')}
            <span
              className="font-semibold cursor-pointer hover:underline px-2"
              style={{ color: getColour('activeText') }}
              onClick={() => navigate(PATHS().LOGIN)}
            >
              {t('login-here')}
            </span>
          </p>
        </div>
      </div>

      <div className="flex justify-center">
        <form
          className="w-full sm:w-[90%] md:max-w-[50%] lg:max-w-[35%]"
          onSubmit={handleSubmit(onSubmit)}
        >
          <p className="mt-1 text-sm text-center leading-6">
            {t('create-a-password-at-least-12-characters-long')}
          </p>

          <div className="p-8 rounded-2xl grid gap-x-8 gap-y-5 pb-6 grid-cols-1 mb-4">
            <Input
              id="password"
              label={t('password')}
              {...register('password')}
              type="password"
              className={cn('block w-full', css(styles.registerContainer))}
              error={String(errors?.password?.message || '')}
            />

            <Input
              id="password_confirmation"
              label={t('password-confirmation')}
              {...register('password_confirmation')}
              type="password"
              className={cn('block w-full', css(styles.registerContainer))}
              error={String(errors?.password_confirmation?.message || '')}
            />

            <div>
              <p className="mt-4 text-sm text-center leading-6">
                {t('need-help-activating-your-account-contact-support')}
                :&nbsp;
                <a
                  href={`mailto:info@scriptix.io`}
                  className={cn(
                    'relative -mr-px inline-flex w-0 flex-1 min-w-fit items-center justify-center gap-x-1 rounded-bl-lg border border-transparent py-2 text-sm font-semibold'
                  )}
                  style={{ color: getColour('activeText') }}
                >
                  Scriptix Support
                </a>
              </p>
            </div>
          </div>

          <div className="p-8 rounded-2xl grid gap-x-8 gap-y-5 pb-6 grid-cols-2 mb-4">
            <Button
              label={t('cancel')}
              size="medium"
              variant="secondary"
              onClick={() => navigate('/login')}
            />
            <Button
              label={isSubmitting ? t('submitting') : t('submit')}
              size="medium"
              variant="primary"
              type="submit"
              disabled={!isValid || isSubmitting || isLoading}
            />
          </div>
        </form>
      </div>

      {passwordValue?.length > 0 && (
        <div className="py-4 flex justify-center items-center">
          <PasswordCriteria password={passwordValue} />
        </div>
      )}
    </div>
  );
};

export default ResetPasswordForm;
